import {
    BaseKeyProvider,
    createKeyMaterialFromBuffer,
    type AudioCaptureOptions,
    type RoomOptions,
    type ScreenShareCaptureOptions,
    type VideoCaptureOptions,
} from 'livekit-client';
import {
    E2EE_KEY_PROVIDER_OPTIONS,
    type MediaE2EE,
    type MediaKeySink,
    type RtcMediaPlatform,
} from '@phonehq/rtc-core';

export type KeyMaterialFactory = (buffer: ArrayBuffer) => Promise<CryptoKey>;

const toArrayBuffer = (key: Uint8Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(key.byteLength);
    new Uint8Array(buffer).set(key);
    return buffer;
};

export class PhqWebKeyProvider extends BaseKeyProvider implements MediaKeySink {
    private readonly createMaterial: KeyMaterialFactory;

    constructor(createMaterial: KeyMaterialFactory = createKeyMaterialFromBuffer) {
        super({...E2EE_KEY_PROVIDER_OPTIONS});
        this.createMaterial = createMaterial;
    }

    async setKey(livekitIdentity: string, key: Uint8Array, index: number): Promise<void> {
        const material = await this.createMaterial(toArrayBuffer(key));
        this.onSetEncryptionKey(material, livekitIdentity, index);
    }
}

export const createE2eeWorker = (): Worker =>
    new Worker(new URL('livekit-client/e2ee-worker', import.meta.url), {type: 'module'});

export const SCREEN_SHARE_CAPTURE_OPTIONS: ScreenShareCaptureOptions = {
    audio: {restrictOwnAudio: true},
    selfBrowserSurface: 'exclude',
    contentHint: 'detail',
};

export interface MediaDevicePreferences {
    readonly audioInputId?: string;
    readonly videoInputId?: string;
    readonly audioOutputId?: string;
}

export interface WebRtcPlatformOptions {
    readonly createWorker?: () => Worker;
    readonly createKeyProvider?: () => PhqWebKeyProvider;
}

const MOBILE_BROWSER = /Android|iPhone|iPad|iPod/i;

const isMobileBrowser = (): boolean => MOBILE_BROWSER.test(navigator.userAgent);

export const audioCaptureFor = (deviceId: string | undefined): AudioCaptureOptions => ({
    echoCancellation: true,
    noiseSuppression: true,
    voiceIsolation: true,
    autoGainControl: !isMobileBrowser(),
    ...(deviceId ? {deviceId} : {}),
});

export const videoCaptureFor = (deviceId: string | undefined): VideoCaptureOptions => (
    deviceId ? {deviceId} : {}
);

export class WebRtcPlatform implements RtcMediaPlatform {
    private worker?: Worker;
    private devices: MediaDevicePreferences = {};

    constructor(private readonly options: WebRtcPlatformOptions = {}) {}

    setDevicePreferences(devices: MediaDevicePreferences): void {
        this.devices = {...devices};
    }

    createE2EE(): MediaE2EE {
        this.releaseWorker();
        const keyProvider = this.options.createKeyProvider ? this.options.createKeyProvider() : new PhqWebKeyProvider();
        const worker = this.options.createWorker ? this.options.createWorker() : createE2eeWorker();
        this.worker = worker;
        return {options: {keyProvider, worker}, sink: keyProvider};
    }

    roomOptions(): Partial<RoomOptions> {
        return {
            audioCaptureDefaults: audioCaptureFor(this.devices.audioInputId),
            videoCaptureDefaults: videoCaptureFor(this.devices.videoInputId),
            ...(this.devices.audioOutputId ? {audioOutput: {deviceId: this.devices.audioOutputId}} : {}),
        };
    }

    screenShareOptions(): ScreenShareCaptureOptions {
        return SCREEN_SHARE_CAPTURE_OPTIONS;
    }

    async afterDisconnect(): Promise<void> {
        this.releaseWorker();
    }

    private releaseWorker(): void {
        this.worker?.terminate();
        this.worker = undefined;
    }
}
